export type ServiceGroup = "Build & Qualify" | "Start & Deliver" | "Quality & Support";

export interface ServiceDetail {
  id: string;
  group: ServiceGroup;
  icon: string;
  title: string;
  summary: string;
  intro: string;
  bullets: string[];
  closing: string;
}

export const SERVICES_META = {
  title: "Clinical Research Site Management Services",
  description:
    "MedCore Research Inc. provides clinical trial site development, feasibility, study start-up, patient recruitment, regulatory coordination, data quality, and site management support for healthcare practices, pharmaceutical and biotechnology companies, and CROs.",
};

export const SERVICES_HERO = {
  title: "Advancing Clinical Research Through Stronger Site Partnerships",
  paragraphs: [
    "MedCore Research Inc. helps community healthcare practices establish and operate sustainable clinical research programs while connecting pharmaceutical companies, biotechnology companies, and Contract Research Organizations with capable, well-supported research sites.",
    "From initial site assessment and study feasibility through start-up, participant recruitment, study conduct, and close-out, we provide structured operational support designed to promote quality, compliance, efficiency, and a positive participant experience.",
  ],
};

export const SERVICES_INTRO = [
  "Successful clinical trials require more than an appropriate protocol. They depend on qualified investigators, suitable patient populations, trained site teams, organized processes, timely communication, and consistent operational oversight.",
  "MedCore Research Inc. brings these elements together.",
  "We collaborate with community-based healthcare practices to develop research-ready sites and support Phase II–IV clinical trials in suitable outpatient therapeutic areas.",
  "For healthcare practices, we reduce the administrative and operational burden associated with entering clinical research. For pharmaceutical and biotechnology companies and CROs, we provide access to supported sites that are prepared to evaluate study opportunities, respond efficiently, and conduct research responsibly.",
];

export const SERVICE_DETAILS: ServiceDetail[] = [
  {
    id: "site-development",
    group: "Build & Qualify",
    icon: "building",
    title: "Site Development and Research Readiness",
    summary:
      "Helping healthcare practices establish the infrastructure, processes, and documentation needed to become research-ready.",
    intro:
      "We help healthcare practices evaluate, establish, and strengthen their clinical research capabilities.",
    bullets: [
      "Assessment of site infrastructure, staffing, patient population, and research potential",
      "Development of professional site profiles",
      "Identification of operational gaps and readiness requirements",
      "Research workflow planning",
      "Support with essential equipment, systems, documentation, and processes",
      "Development and implementation of site-level standard operating procedures",
      "Preparation for qualification visits, site selection visits, and sponsor or CRO assessments",
    ],
    closing:
      "Our objective is to help each partner site present an accurate, professional, and credible overview of its research capabilities.",
  },
  {
    id: "study-feasibility",
    group: "Build & Qualify",
    icon: "file-search",
    title: "Site Profiling and Study Feasibility",
    summary:
      "Providing clear site information, patient-population assessments, and practical feasibility insights.",
    intro:
      "Accurate feasibility is essential for selecting appropriate studies and developing realistic recruitment expectations.",
    bullets: [
      "Evaluation of protocol requirements against site capabilities",
      "Review of investigator qualifications and relevant clinical experience",
      "Assessment of available staff, facilities, equipment, and operational capacity",
      "Analysis of potentially eligible patient populations",
      "Review of anticipated recruitment opportunities and barriers",
      "Assessment of competing studies and standard-of-care considerations",
      "Coordination of feasibility questionnaires and follow-up requests",
      "Development of practical enrollment projections",
    ],
    closing:
      "We aim to provide pharmaceutical and biotechnology companies and CROs with clear, timely, and realistic site information to support informed site-selection decisions.",
  },
  {
    id: "study-matching",
    group: "Build & Qualify",
    icon: "handshake",
    title: "Study Identification and Opportunity Matching",
    summary:
      "Connecting suitable studies with investigators and healthcare practices whose capabilities align with protocol needs.",
    intro:
      "We connect suitable clinical research opportunities with qualified healthcare practices and investigators.",
    bullets: [
      "Presenting relevant site capabilities to pharmaceutical and biotechnology companies and CROs",
      "Identifying studies aligned with the investigator’s experience and patient population",
      "Reviewing preliminary protocol requirements with the site",
      "Coordinating confidentiality agreements and feasibility documentation",
      "Supporting communication between the site and study partners",
      "Helping investigators evaluate the operational requirements of potential studies",
      "Facilitating qualification and site-selection activities",
    ],
    closing:
      "Each study opportunity is evaluated individually. Partner sites retain the ability to review and decide whether a study is appropriate for their practice and patients.",
  },
  {
    id: "study-start-up",
    group: "Start & Deliver",
    icon: "rocket",
    title: "Study Start-Up and Activation",
    summary:
      "Coordinating ethics, essential documents, contracting, training, and activation requirements.",
    intro:
      "MedCore coordinates essential start-up activities to help sites move from selection to activation in an organized and timely manner.",
    bullets: [
      "Collection and maintenance of investigator and site essential documents",
      "Coordination of confidentiality agreements and clinical trial agreements",
      "Budget review and administrative coordination",
      "Support with Research Ethics Board submissions and responses",
      "Coordination of regulatory and institutional documentation",
      "Financial disclosure and investigator documentation support",
      "Site initiation preparation",
      "Study-specific training coordination",
      "Delegation and training log preparation",
      "Development of study-specific workflows and implementation plans",
      "Tracking of outstanding start-up requirements through activation",
    ],
    closing:
      "Our structured start-up process is designed to reduce preventable delays and maintain clear communication among the site, pharmaceutical or biotechnology company, CRO, ethics board, and other study partners.",
  },
  {
    id: "site-management",
    group: "Start & Deliver",
    icon: "workflow",
    title: "Clinical Trial Site Management",
    summary:
      "Supporting organized day-to-day study operations from activation through close-out.",
    intro:
      "Once a study is activated, MedCore provides ongoing operational support throughout the clinical trial lifecycle.",
    bullets: [
      "Coordination of day-to-day study activities",
      "Participant scheduling and visit planning",
      "Maintenance of study trackers and operational logs",
      "Coordination with investigators, research coordinators, monitors, laboratories, pharmacies, and vendors",
      "Essential document management",
      "Monitoring visit preparation and follow-up",
      "Tracking of action items, queries, deviations, and outstanding documentation",
      "Support with investigational product and study-supply coordination",
      "Protocol amendment implementation",
      "Audit, inspection, and close-out preparation",
      "Communication and escalation of operational risks",
    ],
    closing:
      "Our role is to provide the structure and administrative support needed to help investigators and site teams conduct studies efficiently and in accordance with the approved protocol and applicable requirements.",
  },
  {
    id: "patient-recruitment",
    group: "Start & Deliver",
    icon: "user-plus",
    title: "Patient Identification, Recruitment, and Retention",
    summary:
      "Developing ethical, site-specific approaches to identify, enroll, and retain appropriate participants.",
    intro:
      "Effective recruitment planning begins before a site commits to a study. MedCore works with partner sites to develop practical, ethical, and protocol-specific recruitment strategies based on the site’s patient population and available resources.",
    bullets: [
      "Preliminary patient-population assessment",
      "Development of site-specific recruitment plans",
      "Support with privacy-compliant patient identification workflows",
      "Pre-screening coordination based on approved study criteria",
      "Recruitment material coordination",
      "Community and physician-referral engagement, where appropriate",
      "Tracking of screening, enrollment, and screen-failure trends",
      "Identification of recruitment barriers and corrective actions",
      "Participant visit reminders and scheduling support",
      "Retention planning throughout the study",
      "Coordination of approved participant reimbursement processes",
    ],
    closing:
      "All recruitment activities are conducted under the direction of the investigator and in accordance with ethics-approved materials, applicable privacy requirements, and the study protocol.",
  },
  {
    id: "regulatory-ethics",
    group: "Quality & Support",
    icon: "shield-check",
    title: "Regulatory and Ethics Coordination",
    summary:
      "Supporting organized ethics submissions, essential documentation, amendments, renewals, and reporting.",
    intro:
      "Clinical research requires complete, current, and well-organized documentation.",
    bullets: [
      "Preparation and coordination of ethics submission packages",
      "Tracking of ethics approvals, renewals, amendments, and reportable events",
      "Collection and maintenance of essential regulatory documents",
      "Investigator Site File organization and maintenance",
      "Tracking of licenses, curricula vitae, training records, and certifications",
      "Safety communication documentation",
      "Protocol amendment implementation",
      "Continuing review and study-status reporting",
      "Close-out and document-retention planning",
    ],
    closing:
      "We work with investigators and relevant study partners to help ensure that required documentation remains organized, traceable, and inspection-ready.",
  },
  {
    id: "data-quality",
    group: "Quality & Support",
    icon: "file-check",
    title: "Data Quality and Study Documentation",
    summary:
      "Promoting accurate, complete, and timely documentation, data entry, and query resolution.",
    intro:
      "Reliable clinical research depends on accurate, complete, and timely documentation.",
    bullets: [
      "Source-documentation workflow planning",
      "Electronic Data Capture system support",
      "Tracking of data-entry timelines",
      "Query review and resolution follow-up",
      "Review of documentation for completeness and consistency",
      "Protocol deviation tracking",
      "Identification and escalation of recurring quality issues",
      "Maintenance of study-specific quality trackers",
      "Support for monitoring visit findings and follow-up actions",
      "Preparation for database cleaning and study close-out",
    ],
    closing:
      "MedCore promotes a proactive quality culture in which issues are identified early, communicated clearly, and addressed through appropriate corrective and preventive actions.",
  },
  {
    id: "site-training",
    group: "Quality & Support",
    icon: "graduation-cap",
    title: "Investigator and Site-Team Training",
    summary:
      "Preparing investigators and site teams through role-specific and study-specific training support.",
    intro:
      "A well-prepared site team is essential for safe, compliant, and efficient clinical trial delivery.",
    bullets: [
      "Good Clinical Practice",
      "Study protocols and amendments",
      "Informed consent processes",
      "Participant privacy and confidentiality",
      "Safety event identification and reporting",
      "Source documentation and data entry",
      "Study systems and technology platforms",
      "Investigational product and study-supply processes",
      "Role-specific responsibilities",
      "Recruitment and retention workflows",
      "Monitoring, audit, and inspection readiness",
    ],
    closing:
      "Training support is tailored to the study and the responsibilities assigned to each member of the site team.",
  },
  {
    id: "budget-administration",
    group: "Quality & Support",
    icon: "scroll-text",
    title: "Budget and Administrative Coordination",
    summary:
      "Supporting clear study-budget administration, payment tracking, invoicing, and reconciliation.",
    intro:
      "MedCore supports the administrative and financial processes associated with clinical trial delivery.",
    bullets: [
      "Review and coordination of study budgets",
      "Identification of site-level operational requirements",
      "Contract and budget communication support",
      "Tracking of start-up, recruitment, visit, and close-out payments",
      "Review of payment schedules and supporting documentation",
      "Invoice preparation and coordination",
      "Reconciliation of site payments",
      "Tracking of approved pass-through expenses",
      "Administrative support for study vendors and service providers",
    ],
    closing:
      "Our objective is to maintain a clear and organized administrative process so that site teams can focus more effectively on participants and study conduct.",
  },
  {
    id: "performance-oversight",
    group: "Quality & Support",
    icon: "gauge",
    title: "Performance Oversight and Continuous Improvement",
    summary:
      "Using structured trackers and reviews to identify risks, monitor milestones, and improve site performance.",
    intro:
      "Clinical trial performance requires ongoing visibility and timely action.",
    bullets: [
      "Enrollment and screening trackers",
      "Study milestone monitoring",
      "Action-item and issue-management logs",
      "Review of recruitment and retention performance",
      "Monitoring of data-entry and query-resolution timelines",
      "Documentation quality reviews",
      "Identification of operational risks",
      "Escalation and resolution planning",
      "Periodic performance discussions with site teams",
      "Implementation of process improvements and preventive actions",
    ],
    closing:
      "This structured approach helps sites identify challenges early and maintain consistent performance throughout the study.",
  },
];

export const SERVICE_GROUPS = (["Build & Qualify", "Start & Deliver", "Quality & Support"] as const).map(
  (label) => ({
    label,
    items: SERVICE_DETAILS.filter((service) => service.group === label),
  }),
);

export const PARTNER_SECTIONS = [
  {
    id: "healthcare-practices",
    eyebrow: "For Healthcare Practices and Investigators",
    title: "Add Clinical Research to Your Practice Without Building the Program Alone",
    paragraphs: [
      "MedCore helps physicians and healthcare practices enter clinical research through a structured and collaborative partnership.",
      "We support practices with:",
    ],
    bullets: [
      "Research-readiness assessment",
      "Site setup and infrastructure planning",
      "Site profiling and professional presentation",
      "Study identification and opportunity matching",
      "Feasibility and qualification coordination",
      "Regulatory and ethics documentation",
      "Research-team training",
      "Patient recruitment and participant coordination",
      "Day-to-day study administration",
      "Data and documentation support",
      "Financial and payment tracking",
      "Monitoring, audit, and close-out preparation",
    ],
    closing:
      "The investigator remains responsible for medical decisions, participant safety, informed consent, and clinical oversight. MedCore provides the operational framework and administrative support needed to help the site conduct research effectively.",
    cta: "Become a MedCore Partner Site",
    inquiry: "practice",
  },
  {
    id: "study-partners",
    eyebrow: "For Pharmaceutical, Biotechnology, and CRO Partners",
    title: "Connect With Community-Based Sites Supported for Reliable Trial Delivery",
    paragraphs: [
      "MedCore provides pharmaceutical and biotechnology companies and CROs with a responsive point of coordination for identifying and working with community healthcare practices.",
      "Our support includes:",
    ],
    bullets: [
      "Identification of sites aligned with protocol requirements",
      "Clear and detailed site profiles",
      "Timely feasibility coordination",
      "Realistic patient-population assessments",
      "Investigator and site qualification support",
      "Start-up document coordination",
      "Site-level recruitment planning",
      "Operational oversight and issue escalation",
      "Performance and milestone tracking",
      "Consistent communication throughout the study lifecycle",
    ],
    closing:
      "Our goal is to help study partners access appropriate community-based patient populations while maintaining clear expectations, operational visibility, and quality-focused study conduct.",
    cta: "Submit a Study Opportunity",
    inquiry: "study",
  },
];

export const WHY_MEDCORE = [
  {
    icon: "stethoscope",
    title: "Community-Based Research Focus",
    description:
      "We work with healthcare practices that understand their patient communities and can help bring clinical research opportunities closer to participants.",
  },
  {
    icon: "workflow",
    title: "End-to-End Operational Support",
    description:
      "Our services cover the clinical trial lifecycle, from site development and feasibility through activation, study conduct, and close-out.",
  },
  {
    icon: "sparkles",
    title: "Site-Specific Solutions",
    description:
      "Every healthcare practice operates differently. We adapt our processes to the site’s capabilities, staffing model, patient population, and study requirements.",
  },
  {
    icon: "shield-check",
    title: "Quality and Compliance",
    description:
      "We promote organized documentation, timely communication, protocol adherence, participant protection, and early identification of operational risks.",
  },
  {
    icon: "message",
    title: "Transparent Communication",
    description:
      "We maintain clear communication with investigators, site teams, pharmaceutical and biotechnology companies, CROs, ethics boards, and study vendors.",
  },
  {
    icon: "layers",
    title: "Scalable Partnership Model",
    description:
      "Our support can be adapted for an individual clinical trial, a developing research practice, or a growing network of community research sites.",
  },
];

export const HOW_WE_WORK = [
  {
    title: "Assess",
    description:
      "We evaluate the healthcare practice, investigator experience, patient population, infrastructure, and research potential.",
  },
  {
    title: "Prepare",
    description:
      "We help establish the processes, documentation, training, technology, and workflows required for clinical research.",
  },
  {
    title: "Connect",
    description:
      "We present suitable site capabilities to pharmaceutical and biotechnology companies and CROs and identify appropriate study opportunities.",
  },
  {
    title: "Activate",
    description:
      "We coordinate feasibility, qualification, ethics, contracting, training, and other start-up requirements.",
  },
  {
    title: "Support",
    description:
      "We provide ongoing operational, recruitment, documentation, quality, and administrative support throughout the clinical trial lifecycle.",
  },
];

export const PARTNER_LINKS = [
  {
    label: "For Healthcare Practices and Investigators",
    to: "/services#healthcare-practices",
  },
  {
    label: "For Pharmaceutical, Biotechnology, and CRO Partners",
    to: "/services#study-partners",
  },
  {
    label: "Become a MedCore Partner Site",
    to: "/contact?inquiry=practice#contact-form",
  },
  {
    label: "Submit a Study Opportunity",
    to: "/contact?inquiry=study#contact-form",
  },
];
